import React from "react";
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  Stack,
  Chip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { NavigateNext as NextIcon } from "@mui/icons-material";

export interface DashboardCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  badge?: string | number;
  urgent?: boolean;
  buttonText?: string;
  showButton?: boolean;
  disabled?: boolean;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  description,
  icon,
  onClick,
  color = "primary",
  badge,
  urgent = false,
  buttonText,
  showButton = false,
  disabled = false,
}) => {
  const theme = useTheme();

  const getColorWithAlpha = (colorName: string, alphaValue: number) => {
    switch (colorName) {
      case "primary":
        return alpha(theme.palette.primary.main, alphaValue);
      case "secondary":
        return alpha(theme.palette.secondary.main, alphaValue);
      case "error":
        return alpha(theme.palette.error.main, alphaValue);
      case "warning":
        return alpha(theme.palette.warning.main, alphaValue);
      case "info":
        return alpha(theme.palette.info.main, alphaValue);
      case "success":
        return alpha(theme.palette.success.main, alphaValue);
      default:
        return alpha(theme.palette.primary.main, alphaValue);
    }
  };

  return (
    <Card
      sx={{
        height: "100%",
        border: urgent
          ? `2px solid ${theme.palette.warning.main}`
          : `1px solid ${alpha(theme.palette.brand.accent, 0.22)}`,
        borderRadius: 3,
        // One treatment in every mode: paper, an accent hairline and the brand card shadow.
        background: theme.palette.background.paper,
        boxShadow: theme.palette.brand.shadow.card,
        opacity: disabled ? 0.6 : 1,
        transition: theme.transitions.create(["transform", "box-shadow", "border-color"]),
        "&:hover": disabled
          ? {}
          : {
              transform: "translateY(-4px)",
              boxShadow: theme.palette.brand.shadow.raised,
              borderColor: `${color}.main`,
            },
        // Respect users who prefer reduced motion: no lift/animation.
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          "&:hover": { transform: "none" },
        },
        position: "relative",
      }}
    >
      <CardActionArea
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        aria-label={
          badge !== undefined && badge !== "" ? `${title}, ${badge}` : title
        }
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "flex-start",
        }}
      >
      <CardContent
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          p: 3,
        }}
      >
        <Stack spacing={2} sx={{ height: "100%" }}>
          {/* Header with Icon and Badge */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: getColorWithAlpha(color, 0.1),
                color: `${color}.main`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 48,
                minHeight: 48,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {badge && (
                <Chip
                  label={badge}
                  size="small"
                  color={urgent ? "warning" : color}
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  }}
                />
              )}
              {!showButton && (
                // Decorative affordance only — the whole card is the button, so this
                // is a plain icon (not a nested, unlabeled IconButton).
                <Box
                  aria-hidden
                  sx={{ display: "flex", color: "text.secondary", opacity: 0.7 }}
                >
                  <NextIcon fontSize="small" />
                </Box>
              )}
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: "text.primary",
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.4,
                flex: 1
              }}>
              {description}
            </Typography>

            {/* Optional Button */}
            {showButton && buttonText && (
              <Box sx={{ mt: 2, pt: 1 }}>
                <Box
                  sx={{
                    width: "100%",
                    py: 1.5,
                    px: 2,
                    bgcolor: `${color}.main`,
                    color: `${color}.contrastText`,
                    borderRadius: 2,
                    textAlign: "center",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      bgcolor: `${color}.dark`,
                      transform: "scale(1.02)",
                    },
                  }}
                >
                  {buttonText}
                </Box>
              </Box>
            )}
          </Box>
        </Stack>
      </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default DashboardCard;
